import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Save, Loader2, Percent, DollarSign, Wand2, RotateCcw } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const SALE_BADGES = {
  active: ["ON SALE", "bg-primary/15 text-primary border-primary/30", (p) => (p.date_on_sale_to ? `until ${fmtDate(p.date_on_sale_to)}` : "")],
  expired: ["SALE EXPIRED", "bg-muted text-muted-foreground border-border", (p) => `ended ${fmtDate(p.date_on_sale_to)}`],
  scheduled: ["SALE SCHEDULED", "bg-amber-500/15 text-amber-400 border-amber-500/30", (p) => `starts ${fmtDate(p.date_on_sale_from)}`],
};

export const SaleBadge = ({ p }) => {
  const cfg = SALE_BADGES[p.sale_status];
  if (!cfg) return null;
  const [label, cls, note] = cfg;
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`sale-status-${p.id}`}>
      <Badge className={`border text-[10px] ${cls}`}>{label}</Badge>
      <span className="text-[10px] text-muted-foreground">{note(p)}</span>
    </span>
  );
};


export default function ProductsGrid({ site }) {
  const [rows, setRows] = useState([]);
  const [original, setOriginal] = useState({});
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [tag, setTag] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkType, setBulkType] = useState("percentage");
  const [bulkValue, setBulkValue] = useState("15");
  const [applying, setApplying] = useState(false);

  const retryTimer = useRef(null);
  const gridReq = useRef(0);
  const load = useCallback(async () => {
    const id = ++gridReq.current;
    clearTimeout(retryTimer.current);
    retryTimer.current = null;
    let retrying = false;
    setLoading(true);
    setLoadError("");
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== "all") params.category = category;
      if (tag !== "all") params.tag = tag;
      const r = await api.get(`/sites/${site.id}/products`, { params });
      if (id !== gridReq.current) {
        retrying = true;
        return;
      }
      setRows(r.data);
      const orig = {};
      r.data.forEach((p) => (orig[p.id] = { regular_price: p.regular_price, sale_price: p.sale_price }));
      setOriginal(orig);
      setSelected(new Set());
    } catch (e) {
      if (id !== gridReq.current) {
        retrying = true;
        return;
      }
      if (e.response?.status === 503) {
        retrying = true;
        retryTimer.current = setTimeout(() => loadRef.current(), 4000);
        return;
      }
      setRows([]);
      setLoadError(apiError(e));
    } finally {
      if (!retrying) setLoading(false);
    }
  }, [site.id, search, category, tag]);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => () => clearTimeout(retryTimer.current), []);

  const loadMeta = useCallback(async () => {
    try {
      const [c, t] = await Promise.all([
        api.get(`/sites/${site.id}/categories`),
        api.get(`/sites/${site.id}/tags`),
      ]);
      setCategories(c.data);
      setTags(t.data);
    } catch (e) {
      /* ignore */
    }
  }, [site.id]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);
  useEffect(() => {
    load();
  }, [load]);

  const setField = (id, field, value) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const isDirty = (r) =>
    original[r.id] &&
    (original[r.id].regular_price !== r.regular_price || original[r.id].sale_price !== r.sale_price);

  const saveRow = async (r) => {
    setSavingId(r.id);
    try {
      const body = {};
      if (original[r.id].regular_price !== r.regular_price) body.regular_price = r.regular_price;
      if (original[r.id].sale_price !== r.sale_price) body.sale_price = r.sale_price;
      const res = await api.put(`/sites/${site.id}/products/${r.id}`, body);
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, sale_status: res.data.sale_status, date_on_sale_from: res.data.date_on_sale_from, date_on_sale_to: res.data.date_on_sale_to } : x)));
      setOriginal((o) => ({ ...o, [r.id]: { regular_price: r.regular_price, sale_price: r.sale_price } }));
      toast.success(`${r.name} saved`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingId(null);
    }
  };

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAll = () =>
    setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));

  const applyBulk = async () => {
    const val = parseFloat(bulkValue);
    if (isNaN(val)) return toast.error("Enter a valid number");
    const targets = rows.filter((r) => selected.has(r.id) && r.regular_price);
    if (!targets.length) return toast.error("Select products with a regular price");
    const items = targets.map((r) => {
      const reg = parseFloat(r.regular_price);
      let sale = bulkType === "percentage" ? reg * (1 - val / 100) : reg - val;
      sale = Math.max(sale, 0);
      return { id: r.id, sale_price: sale.toFixed(2) };
    });
    setApplying(true);
    try {
      await api.post(`/sites/${site.id}/products/bulk`, { items });
      toast.success(`Sale price applied to ${items.length} products`);
      setBulkOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplying(false);
    }
  };

  const clearBulkSale = async () => {
    const targets = rows.filter((r) => selected.has(r.id));
    if (!targets.length) return;
    try {
      await api.post(`/sites/${site.id}/products/bulk`, {
        items: targets.map((r) => ({ id: r.id, sale_price: "" })),
      });
      toast.success("Sale price cleared");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search name or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="product-search-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[160px]" data-testid="category-filter-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name} ({c.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger className="w-[160px]" data-testid="tag-filter-select">
              <SelectValue placeholder="Tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {tags.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name} ({t.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <Button variant="ghost" onClick={clearBulkSale} data-testid="clear-sale-button">
              <RotateCcw className="w-4 h-4" /> Clear sale
            </Button>
          )}
          <Button
            onClick={() => setBulkOpen(true)}
            disabled={selected.size === 0}
            data-testid="bulk-edit-trigger-button"
          >
            <Wand2 className="w-4 h-4" /> Bulk discount ({selected.size})
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="w-10">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Product</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead className="hidden md:table-cell">Categories</TableHead>
                <TableHead className="hidden lg:table-cell">Tags</TableHead>
                <TableHead className="w-28">Regular</TableHead>
                <TableHead className="w-28">Sale</TableHead>
                <TableHead className="w-20 text-right">Save</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin inline" />
                  </TableCell>
                </TableRow>
              ) : loadError ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12" data-testid="products-load-error">
                    <p className="text-destructive text-sm mb-3">Couldn't load products from this store: {loadError}</p>
                    <Button variant="outline" size="sm" onClick={load} data-testid="products-retry-button">
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    No products found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} className="border-border" data-testid={`product-row-${r.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggleSelect(r.id)}
                        data-testid={`select-product-${r.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {r.name}
                        <SaleBadge p={r} />
                      </div>
                    </TableCell>
                    <TableCell className="font-mono-num text-xs text-muted-foreground">{r.sku || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {r.categories.map((c) => (
                          <Badge key={c.id} variant="secondary" className="text-[10px]">
                            {c.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {r.tags.map((t) => (
                          <Badge key={t.id} variant="outline" className="text-[10px]">
                            {t.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={r.regular_price}
                        onChange={(e) => setField(r.id, "regular_price", e.target.value)}
                        className="h-8 font-mono-num text-sm"
                        data-testid={`regular-price-input-${r.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={r.sale_price}
                        onChange={(e) => setField(r.id, "sale_price", e.target.value)}
                        className={`h-8 font-mono-num text-sm ${r.sale_status === "expired" ? "line-through text-muted-foreground" : ""}`}
                        placeholder="—"
                        data-testid={`sale-price-input-${r.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant={isDirty(r) ? "default" : "ghost"}
                        className="h-8 w-8"
                        disabled={!isDirty(r) || savingId === r.id}
                        onClick={() => saveRow(r)}
                        data-testid={`save-product-${r.id}`}
                      >
                        {savingId === r.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Bulk dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk discount — {selected.size} products</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button
                variant={bulkType === "percentage" ? "default" : "outline"}
                onClick={() => setBulkType("percentage")}
                className="flex-1"
                data-testid="bulk-type-percentage"
              >
                <Percent className="w-4 h-4" /> Percentage
              </Button>
              <Button
                variant={bulkType === "fixed" ? "default" : "outline"}
                onClick={() => setBulkType("fixed")}
                className="flex-1"
                data-testid="bulk-type-fixed"
              >
                <DollarSign className="w-4 h-4" /> Fixed amount
              </Button>
            </div>
            <div className="space-y-2">
              <Label>{bulkType === "percentage" ? "Discount %" : "Reduce price by"}</Label>
              <Input
                value={bulkValue}
                onChange={(e) => setBulkValue(e.target.value)}
                type="number"
                className="font-mono-num"
                data-testid="bulk-value-input"
              />
            </div>
            <div className="rounded-md bg-secondary p-3 max-h-40 overflow-y-auto space-y-1 text-sm">
              {rows
                .filter((r) => selected.has(r.id) && r.regular_price)
                .slice(0, 8)
                .map((r) => {
                  const reg = parseFloat(r.regular_price);
                  const val = parseFloat(bulkValue) || 0;
                  let sale = bulkType === "percentage" ? reg * (1 - val / 100) : reg - val;
                  sale = Math.max(sale, 0);
                  return (
                    <div key={r.id} className="flex justify-between font-mono-num text-xs">
                      <span className="text-muted-foreground">{r.name}</span>
                      <span>
                        <span className="line-through text-muted-foreground">{reg.toFixed(2)}</span>{" "}
                        <span className="text-primary">{sale.toFixed(2)}</span>
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button onClick={applyBulk} disabled={applying} data-testid="bulk-apply-button">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Apply sale price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
